import { readLink, readLinkCreative, readLinkVariant } from "@/features/links/attribution";
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { browserTimezone } from '@/lib/zonedTime';
import { clearImpersonationStorage, onImpersonationChange, readImpersonation } from '@/lib/impersonationStorage';
import { supabase } from '@/integrations/supabase/client';
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/env';
import { getBaseUrl } from '@/lib/rssUrl';
import { syncSharedAuthCookie, watchSharedAuthCookie } from '@/lib/sharedAuthCookie';
import { resolveEffectivePlanKey } from '@/lib/effectivePlan';

import { toast } from 'sonner';
import { clearAllVoicePrefs } from '@/features/voice-studio/voicePrefs';
import type {
  AuthContextType,
  Profile,
  Subscription,
  OnboardingProgress,
  AppRole,
  SubscriptionPlan,
  SignUpResult,
} from '@/types/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [onboardingProgress, setOnboardingProgress] = useState<OnboardingProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Synchronously detect recovery token in URL hash before any async event fires
  const [passwordRecoveryActive, setPasswordRecoveryActive] = useState(() => {
    const hash = window.location.hash;
    return hash.includes('type=recovery');
  });
  // Use ref for wasAuthenticated to avoid stale closure in onAuthStateChange
  const wasAuthenticatedRef = useRef(false);

  // Impersonation state. The marker lives in localStorage because the auth
  // session it describes is shared by every tab (see impersonationStorage).
  const getImpersonationData = readImpersonation;
  const [impersonationData, setImpersonationData] = useState(getImpersonationData);
  const isImpersonating = !!impersonationData;
  const impersonatingAs: string | null = impersonationData?.targetUserName ?? null;

  // Single helper to clear stale impersonation state everywhere
  const clearImpersonation = useCallback(() => {
    clearImpersonationStorage();
    setImpersonationData(null);
  }, []);

  // Another tab started or ended impersonation — that tab swapped the shared
  // auth session, so this tab must reflect the same impersonation state.
  useEffect(() => onImpersonationChange(() => setImpersonationData(readImpersonation())), []);


  const isAuthenticated = !!user && !!session;

  // Ref to track the last hydrated user id to avoid redundant fetches on focus re-emits
  const lastHydratedUserIdRef = useRef<string | null>(null);
  // Synchronous dedup guard for welcome email sends
  const welcomeEmailSentForRef = useRef<string | null>(null);
  // Synchronous ref to prevent re-entry into post-auth side effects (welcome email, admin notify)
  // Set IMMEDIATELY on first qualifying SIGNED_IN event, before any async work
  const postAuthHandledForRef = useRef<string | null>(null);

  const supabaseUrl = SUPABASE_URL;
  const supabaseAnonKey = SUPABASE_PUBLISHABLE_KEY;

  // Fetch all user data via direct fetch to edge function (bypasses Supabase client auth lock)
  const fetchUserDataDirect = useCallback(async (accessToken: string): Promise<boolean> => {
    console.log('[Auth] fetchUserDataDirect start');
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/get-user-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        console.error('[Auth] fetchUserDataDirect HTTP error:', res.status);
        return false;
      }
      const data = await res.json();
      console.log('[Auth] fetchUserDataDirect success');
      setProfile(data.profile ?? null);
      setSubscription(data.subscription ?? null);
      setRoles(data.roles ?? []);
      setOnboardingProgress(data.onboardingProgress ?? null);
      return true;
    } catch (err) {
      console.error('[Auth] fetchUserDataDirect exception:', err);
      return false;
    }
  }, [supabaseUrl, supabaseAnonKey]);

  // Fetch profile data (used for refreshProfile — safe to use supabase client outside auth callback)
  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;
      const res = await fetch(`${supabaseUrl}/functions/v1/get-user-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) return null;
      const data = await res.json();
      setSubscription(data.subscription ?? null);
      setRoles(data.roles ?? []);
      setOnboardingProgress(data.onboardingProgress ?? null);
      return data.profile as Profile;
    } catch {
      return null;
    }
  }, [supabaseUrl, supabaseAnonKey]);

  // Fetch subscription data (used for refreshSubscription)
  const fetchSubscription = useCallback(async (userId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;
      const res = await fetch(`${supabaseUrl}/functions/v1/get-user-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) return null;
      const data = await res.json();
      setProfile(data.profile ?? null);
      setRoles(data.roles ?? []);
      setOnboardingProgress(data.onboardingProgress ?? null);
      return data.subscription as unknown as Subscription;
    } catch {
      return null;
    }
  }, [supabaseUrl, supabaseAnonKey]);

  // Fetch onboarding progress (used for updateOnboardingProgress)
  const fetchOnboardingProgress = useCallback(async (userId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;
      const res = await fetch(`${supabaseUrl}/functions/v1/get-user-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.onboardingProgress as unknown as OnboardingProgress;
    } catch {
      return null;
    }
  }, [supabaseUrl, supabaseAnonKey]);

  const buildWelcomeEmailPayload = useCallback((
    authUser: User,
    currentProfile: Pick<Profile, 'display_name' | 'can_create_content'> | null,
  ) => {
    const templateType = currentProfile?.can_create_content === false
      ? 'welcome_under_review'
      : 'welcome';

    return {
      type: templateType as 'welcome' | 'welcome_under_review',
      recipientEmail: authUser.email || '',
      variables: {
        user_name: currentProfile?.display_name || 'there',
        login_link: `${getBaseUrl()}/`,
        user_email: authUser.email || '',
        support_link: `${getBaseUrl()}/portal/help`,
      },
    };
  }, []);

  // Send branded welcome email on first login (fire and forget)
  const sendWelcomeEmailIfNeeded = useCallback(async (authUser: User, includeInvited: boolean = false) => {
    // Synchronous guard: prevent duplicate sends from rapid auth events
    if (welcomeEmailSentForRef.current === authUser.id) return;
    welcomeEmailSentForRef.current = authUser.id;

    try {
      // Atomic claim via DB RPC: flips welcome_email_sent from false→true
      // and returns profile data. If no row is returned, another auth event
      // already claimed it — exit.
      const { data: rows } = await supabase.rpc('claim_welcome_email_send', { _include_invited: includeInvited });
      const claimed = rows?.[0] ?? null;

      if (!claimed) return;

      const { error: emailError } = await supabase.functions.invoke('send-auth-email', {
        body: buildWelcomeEmailPayload(authUser, claimed),
      });

      if (emailError) {
        throw emailError;
      }

      // Notify admins about new user registration (fire and forget)
      try {
        await supabase.functions.invoke('notify-pending-approval', {
          body: {
            type: 'new_user_registered',
            userName: claimed.display_name || authUser.email?.split('@')[0] || 'Unknown',
            userEmail: authUser.email || '',
            signupDate: new Date().toISOString(),
            userId: authUser.id,
          },
        });
      } catch {
        // Silent fail — admin notification is non-critical
      }
    } catch {
      // Reset ref on failure so retry is possible on next session event
      welcomeEmailSentForRef.current = null;
    }
  }, [buildWelcomeEmailPayload]);

  // Initialize auth state — use ONLY onAuthStateChange (INITIAL_SESSION handles first load)
  useEffect(() => {
    let loadingResolved = false;
    const resolveLoading = () => {
      if (!loadingResolved) {
        loadingResolved = true;
        setIsLoading(false);
      }
    };

    // HARD TIMEOUT: guarantee isLoading resolves within 15 seconds no matter what
    const hardTimeout = setTimeout(() => {
      if (!loadingResolved) {
        console.warn('[Auth] Hard timeout reached — forcing isLoading to false');
        resolveLoading();
      }
    }, 15000);

    const stopSharedWatch = watchSharedAuthCookie();
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('[Auth] onAuthStateChange:', event, !!newSession?.user);
        syncSharedAuthCookie(event, newSession);

        // Detect password recovery flow
        if (event === 'PASSWORD_RECOVERY') {
          setPasswordRecoveryActive(true);
        }

        // Detect session expiration (signed out after being authenticated)
        if (event === 'SIGNED_OUT' && wasAuthenticatedRef.current) {
          // Skip session-expired flag during impersonation switch-back
          if (!readImpersonation()) {
            setSessionExpired(true);
          }
        }

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          wasAuthenticatedRef.current = true;
          setSessionExpired(false);

          const userId = newSession.user.id;
          const isNewUser = lastHydratedUserIdRef.current !== userId;
          // Use postAuthHandledForRef (set synchronously) to gate side effects,
          // NOT lastHydratedUserIdRef (set after async hydration completes).
          const shouldRunPostAuthSideEffects = (event === 'SIGNED_IN' || event === 'USER_UPDATED') && postAuthHandledForRef.current !== userId;
          const isInitial = event === 'INITIAL_SESSION';

          // Mark immediately BEFORE any async work to prevent re-entry
          if (shouldRunPostAuthSideEffects) {
            postAuthHandledForRef.current = userId;
          }

          // Skip redundant hydration for focus-driven re-emits of the same user
          if (!isNewUser && !isInitial && event !== 'USER_UPDATED' && event !== 'PASSWORD_RECOVERY') {
            console.log('[Auth] Skipping redundant hydration for event:', event);
            return;
          }

          // Default OAuth and signup users to "remembered" (they never see the checkbox)
          if (shouldRunPostAuthSideEffects && !localStorage.getItem('podgo_remember_me')) {
            localStorage.setItem('podgo_remember_me', 'true');
          }

          console.log('[Auth] Starting fetchUserData from listener, event:', event);

          // Use direct fetch with the access token from the callback (avoids Supabase client lock)
          const accessToken = newSession.access_token;
          setTimeout(() => {
            fetchUserDataDirect(accessToken).then((success) => {
              if (success) {
                lastHydratedUserIdRef.current = userId;
                console.log('[Auth] fetchUserData complete, user hydrated');
              } else {
                console.warn('[Auth] fetchUserData failed, data may be incomplete');
              }
              if (shouldRunPostAuthSideEffects) {
                // For USER_UPDATED (password set after invite), include invited users
                const includeInvited = event === 'USER_UPDATED';
                sendWelcomeEmailIfNeeded(newSession.user, includeInvited);
              }
            }).catch((err) => {
              console.error('[Auth] fetchUserData failed:', err);
            }).finally(() => {
              resolveLoading();
            });
          }, 0);
        } else {
          lastHydratedUserIdRef.current = null;
          postAuthHandledForRef.current = null;
          setProfile(null);
          setSubscription(null);
          setRoles([]);
          setOnboardingProgress(null);
          clearImpersonation();
          resolveLoading();
          console.log('[Auth] No session, cleared state');
        }
      }
    );

    return () => {
      clearTimeout(hardTimeout);
      authSubscription.unsubscribe();
      stopSharedWatch();
    };
  }, [fetchUserDataDirect, sendWelcomeEmailIfNeeded, clearImpersonation]);

  // Sign in with email and password
  const signIn = async (email: string, password: string, rememberMe: boolean = false) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    
    if (!error) {
      // Store remember me preference
      if (rememberMe) {
        localStorage.setItem('podgo_remember_me', 'true');
      } else {
        localStorage.removeItem('podgo_remember_me');
      }
    }
    
    return { error: error as Error | null };
  };

  // Sign up with email and password
  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    podcastShowName: string,
    termsAccepted: boolean,
    smsOptIn: boolean = false,
    phone: string = '',
    hasExistingRss: boolean = false,
    existingRssUrl: string = '',
    rssFeedTitle?: string,
    rssFeedImageUrl?: string,
    rssFeedEmail?: string,
    rssFeedEpisodeCount?: number,
    rssFeedDescription?: string
  ): Promise<SignUpResult> => {
    const redirectUrl = `${getBaseUrl()}/`;

    // Pull referral attribution captured from `?ref=CODE` (see SignUpForm)
    let referredBy: string | undefined;
    let referralUrl: string | undefined;
    try {
      const raw = sessionStorage.getItem('podgo_referral');
      if (raw) {
        const parsed = JSON.parse(raw);
        referredBy = parsed?.referred_by || undefined;
        referralUrl = parsed?.referral_url || undefined;
      }
    } catch {
      // ignore
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          last_name: lastName,
          podcast_show_name: podcastShowName,
          display_name: `${firstName} ${lastName}`.trim(),
          terms_accepted: termsAccepted,
          sms_opt_in: smsOptIn,
          phone,
          has_existing_rss: hasExistingRss,
          existing_rss_url: existingRssUrl || undefined,
          rss_feed_title: hasExistingRss ? rssFeedTitle : undefined,
          rss_feed_image_url: hasExistingRss ? rssFeedImageUrl : undefined,
          rss_feed_email: hasExistingRss ? rssFeedEmail : undefined,
          rss_feed_episode_count: hasExistingRss ? rssFeedEpisodeCount : undefined,
          rss_feed_description: hasExistingRss ? rssFeedDescription : undefined,
          referred_by: referredBy,
          referral_url: referralUrl,
          link_slug: readLink() ?? new URLSearchParams(window.location.search).get('link')?.toLowerCase() ?? undefined,
          link_creative: readLinkCreative() ?? new URLSearchParams(window.location.search).get('lc')?.toLowerCase() ?? undefined,
          link_variant: readLinkVariant() ?? new URLSearchParams(window.location.search).get('lv')?.toLowerCase() ?? undefined,
        },
      },
    });
    if (!error) {
      try { sessionStorage.removeItem('podgo_referral'); } catch { /* ignore */ }
    }
    return {
      error: error as Error | null,
      user: data.user ?? null,
      session: data.session ?? null,
    };
  };

  // Sign out
  const signOut = useCallback(async () => {
    localStorage.removeItem('podgo_remember_me');
    clearAllVoicePrefs();
    clearImpersonation();
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setSubscription(null);
    setRoles([]);
    setOnboardingProgress(null);
    setIsLoading(false);
  }, [clearImpersonation]);

  // Request password reset
  const resetPasswordRequest = async (email: string) => {
    const redirectUrl = `${getBaseUrl()}/auth/reset-password`;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });

    return { error: error as Error | null };
  };

  // Reset password with new password
  const resetPassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });

    if (!error && user?.email) {
      try {
        const currentProfile = profile ?? await fetchProfile(user.id);

        if (currentProfile?.invited_by_admin) {
          const { error: emailError } = await supabase.functions.invoke('send-auth-email', {
            body: buildWelcomeEmailPayload(user, currentProfile),
          });

          if (emailError) {
            throw emailError;
          }
        }
      } catch (welcomeError) {
        console.error('[Auth] resetPassword welcome email fallback failed:', welcomeError);
      }
    }

    return { error: error as Error | null };
  };

  // One OAuth path for every provider, straight through PODGO's own auth project,
  // so sessions, profile creation and onboarding stay identical to email login.
  // The provider returns to /auth, which keeps the (already validated) redirect intent.
  const signInWithProvider = async (provider: 'google' | 'facebook' | 'apple' | 'spotify' | 'azure' | 'linkedin_oidc', returnPath?: string | null) => {
    const back = new URL('/auth', getBaseUrl());
    back.searchParams.set('tab', 'login');
    if (returnPath && returnPath.startsWith('/') && !returnPath.startsWith('//')) {
      back.searchParams.set('redirect', returnPath);
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      // Microsoft (Azure) only returns an email address when the scope is asked for.
      options: { redirectTo: back.toString(), ...(provider === 'azure' ? { scopes: 'email' } : {}) },
    });
    return { error: error as Error | null };
  };
  const signInWithGoogle = (returnPath?: string | null) => signInWithProvider('google', returnPath);
  const signInWithFacebook = (returnPath?: string | null) => signInWithProvider('facebook', returnPath);
  const signInWithApple = (returnPath?: string | null) => signInWithProvider('apple', returnPath);
  const signInWithSpotify = (returnPath?: string | null) => signInWithProvider('spotify', returnPath);
  const signInWithMicrosoft = (returnPath?: string | null) => signInWithProvider('azure', returnPath);
  const signInWithLinkedIn = (returnPath?: string | null) => signInWithProvider('linkedin_oidc', returnPath);

  // Capture the browser timezone once, so campaigns can be delivered at a
  // sensible local hour for this user.
  useEffect(() => {
    if (!user || !profile || profile.timezone) return;
    const tz = browserTimezone();
    if (!tz) return;
    void supabase.from('profiles').update({ timezone: tz } as never).eq('id', user.id);
  }, [user, profile]);

  // Refresh profile data
  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
    }
  };

  // Refresh subscription data
  const refreshSubscription = async () => {
    if (user) {
      const subscriptionData = await fetchSubscription(user.id);
      setSubscription(subscriptionData);
    }
  };

  // Check if user has a specific role
  const hasRole = (role: AppRole) => roles.includes(role);

  // Plan gates follow the *effective* plan so comp/override accounts get the
  // same features their limits already allow.
  const isBusinessPlan = () => resolveEffectivePlanKey(subscription) === 'business';

  // Check if user is on Pro plan
  const isProPlan = () => resolveEffectivePlanKey(subscription) === 'pro';


  // Update profile
  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error('Not authenticated') };

    const { error } = await supabase
      .from('profiles')
      .update(updates as never)
      .eq('id', user.id);

    if (!error) {
      await refreshProfile();
    }

    return { error: error as Error | null };
  };

  // Update subscription
  const updateSubscription = async (plan: SubscriptionPlan) => {
    if (!user) return { error: new Error('Not authenticated') };

    const teamSeatsLimit = plan === 'business' ? 5 : 1;

    const { error } = await supabase
      .from('subscriptions')
      .upsert(
        {
          user_id: user.id,
          plan,
          status: 'active',
          team_seats_limit: teamSeatsLimit,
        },
        { onConflict: 'user_id' }
      );

    if (!error) {
      await refreshSubscription();
    }

    return { error: error as Error | null };
  };

  // Update onboarding progress
  const updateOnboardingProgress = async (updates: Partial<OnboardingProgress>) => {
    if (!user) return { error: new Error('Not authenticated') };

    const { error } = await supabase
      .from('onboarding_progress')
      .upsert({ user_id: user.id, ...updates }, { onConflict: 'user_id' });

    if (!error) {
      const onboardingData = await fetchOnboardingProgress(user.id);
      setOnboardingProgress(onboardingData);
    }

    return { error: error as Error | null };
  };

  // Switch back from impersonation
  const switchBack = async () => {
    const data = getImpersonationData();
    if (!data?.adminRefreshToken) {
      // No valid impersonation data — just clean up stale state
      clearImpersonation();
      await supabase.auth.signOut();
      toast.error('Admin session expired. Please log in again.');
      window.location.href = '/auth';
      return;
    }

    // Early no-op: if already logged in as the admin, just clear state and redirect
    if (user?.id && data.adminUserId && user.id === data.adminUserId) {
      console.log('[Auth] switchBack: already logged in as admin, clearing stale impersonation state');
      clearImpersonation();
      window.location.href = '/admin/users';
      return;
    }

    try {
      // Primary: setSession with both stored tokens (no sign-out first to preserve tokens)
      if (data.adminAccessToken) {
        const { error } = await supabase.auth.setSession({
          access_token: data.adminAccessToken,
          refresh_token: data.adminRefreshToken,
        });
        if (!error) {
          console.log('[Auth] switchBack succeeded via setSession');
          clearImpersonation();
          window.location.href = '/admin/users';
          return;
        }
        console.warn('[Auth] setSession failed, falling back to refreshSession:', error.message);
      } else {
        console.log('[Auth] No adminAccessToken stored, using refreshSession directly');
      }

      // Fallback: refreshSession
      const { error } = await supabase.auth.refreshSession({
        refresh_token: data.adminRefreshToken,
      });
      if (error) throw error;
      console.log('[Auth] switchBack succeeded via refreshSession');
      clearImpersonation();
      window.location.href = '/admin/users';
    } catch (error) {
      console.error('[Auth] switchBack failed:', error);
      clearImpersonation();
      await supabase.auth.signOut();
      toast.error('Admin session expired. Please log in again.');
      window.location.href = '/auth';
    }
  };

  const clearPasswordRecovery = useCallback(() => {
    setPasswordRecoveryActive(false);
  }, []);

  const value: AuthContextType = {
    user,
    session,
    profile,
    subscription,
    roles,
    onboardingProgress,
    isLoading,
    isAuthenticated,
    sessionExpired,
    passwordRecoveryActive,
    isImpersonating,
    impersonatingAs,
    signIn,
    signUp,
    signOut,
    resetPasswordRequest,
    resetPassword,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    signInWithSpotify,
    signInWithMicrosoft,
    signInWithLinkedIn,
    refreshProfile,
    refreshSubscription,
    hasRole,
    isBusinessPlan,
    isProPlan,
    updateProfile,
    updateSubscription,
    updateOnboardingProgress,
    switchBack,
    clearPasswordRecovery,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
